import { useState, useEffect, useCallback } from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { Loader2, RotateCcw, Phone, AlertTriangle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useNavigate } from 'react-router-dom';
import KpiStrip from './KpiStrip';
import { api } from '@/api/apiClient';
import { cur } from '@/lib/currency';

function fmt(n) { return Number(n || 0).toLocaleString('ar-EG', { minimumFractionDigits: 0, maximumFractionDigits: 0 }); }
const TOOLTIP = { contentStyle: { background: 'white', border: '1px solid #e5e7eb', borderRadius: '12px', fontSize: '12px', direction: 'rtl' } };

export default function CustomersReport({ dateFrom, dateTo }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const navigate = useNavigate();

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    const res = await api.get('/reports/customers', { date_from: dateFrom, date_to: dateTo });
    if (res?.success) setData(res.data);
    else setError(res?.error || 'فشل تحميل تقرير العملاء');
    setLoading(false);
  }, [dateFrom, dateTo]);

  useEffect(() => { load(); }, [dateFrom, dateTo]);

  if (error) return (
    <div className="p-6 text-center space-y-3">
      <p className="text-red-500">{error}</p>
      <Button onClick={load} variant="outline" size="sm"><RotateCcw className="w-4 h-4 ml-1" />إعادة</Button>
    </div>
  );

  const kpis = data ? [
    { label: 'إجمالي العملاء', value: fmt(data.kpis.total_active), suffix: 'عميل', to: '/customers' },
    { label: 'عملاء جدد', value: fmt(data.kpis.new_customers), suffix: 'عميل', to: '/customers' },
    { label: 'فاعلون في الفترة', value: fmt(data.kpis.active_in_period), suffix: 'عميل', to: '/customers' },
    { label: 'متوسط قيمة العميل', value: fmt(data.kpis.avg_order_value), suffix: cur() },
  ] : [];

  return (
    <div className="space-y-4">
      <KpiStrip kpis={kpis} loading={loading && !data} />

      {data?.top_customers?.length > 0 && (
        <div className="bg-card border border-border rounded-2xl p-4">
          <h3 className="font-bold text-foreground mb-3">أفضل العملاء — مبيعات الفترة</h3>
          <ResponsiveContainer width="100%" height={Math.max(160, data.top_customers.length * 36)}>
            <BarChart data={data.top_customers} layout="vertical">
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis type="number" tick={{ fontSize: 9 }} tickFormatter={v => (v / 1000) + 'k'} />
              <YAxis type="category" dataKey="name" tick={{ fontSize: 9 }} width={110} />
              <Tooltip contentStyle={TOOLTIP.contentStyle} formatter={v => [fmt(v) + ` ${cur()}`, 'مبيعات']} />
              <Bar dataKey="total" fill="#1E40AF" radius={[0, 6, 6, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}

      {data?.at_risk?.length > 0 && (
        <div className="bg-amber-50 border border-amber-200 rounded-2xl p-4">
          <div className="flex items-center gap-2 mb-3">
            <AlertTriangle className="w-4 h-4 text-amber-600" />
            <h3 className="font-bold text-amber-800">عملاء في خطر — لم يطلبوا منذ فترة ({data.at_risk.length})</h3>
          </div>
          <div className="space-y-2">
            {data.at_risk.slice(0, 8).map(c => (
              <div key={c.id} className="flex items-center gap-3 bg-white rounded-xl px-3 py-2 border border-amber-100">
                <div className="w-8 h-8 bg-amber-100 rounded-lg flex items-center justify-center flex-shrink-0">
                  <span className="text-amber-700 font-bold text-sm">{c.name?.charAt(0)}</span>
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-semibold text-sm text-foreground truncate">{c.name}</p>
                  <p className="text-xs text-muted-foreground">آخر مشتريات: {fmt(c.last_total)} {cur()}</p>
                </div>
                <Button onClick={() => navigate(`/visits/new?customer_id=${c.id}&customer_name=${encodeURIComponent(c.name)}`)}
                  size="sm" variant="outline" className="h-7 text-xs rounded-lg border-amber-300 text-amber-700 hover:bg-amber-100 flex-shrink-0">
                  زيارة
                </Button>
              </div>
            ))}
          </div>
        </div>
      )}

      {data?.new_customers?.length > 0 && (
        <div className="bg-card border border-border rounded-2xl p-4">
          <h3 className="font-bold text-foreground mb-3">🆕 عملاء جدد في الفترة ({data.new_customers.length})</h3>
          <div className="space-y-2">
            {data.new_customers.slice(0, 10).map(c => (
              <div key={c.id} className="flex items-center gap-3 py-1.5 border-b border-border last:border-0">
                <div className="w-7 h-7 bg-primary/10 rounded-lg flex items-center justify-center flex-shrink-0">
                  <span className="text-primary font-bold text-xs">{c.name?.charAt(0)}</span>
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-semibold text-sm text-foreground truncate">{c.name}</p>
                  {c.phone && (
                    <div className="flex items-center gap-1">
                      <Phone className="w-3 h-3 text-muted-foreground" />
                      <p className="text-xs text-muted-foreground">{c.phone}</p>
                    </div>
                  )}
                </div>
                <p className="text-xs text-muted-foreground flex-shrink-0">{c.created}</p>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}